import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatWithSystemLocale } from '../../utils/dateUtils';
import CurrencyInput from "../../components/CurrencyInput";
import FormattedCurrency from "../../components/FormattedCurrency";

import { useNotification } from "../../contexts/NotificationContext";
import { Bug } from "lucide-react";
import { getApiUrl } from "../../config/api";

export default function Deworming() {
  const { id: horseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    product_used: "",
    administered_date: "",
    fecal_egg_count_result: "",
    notes: "",
    amount: ""
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  // Check for edit parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    if (editId && records.length > 0) {
      const recordToEdit = records.find(rec => rec.id.toString() === editId);
      if (recordToEdit) {
        startEdit(recordToEdit);
        // Clean up URL by removing the edit parameter
        navigate(`/horses/${horseId}/deworming`, { replace: true });
      }
    }
  }, [records, location.search, navigate, horseId]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${horseId}/deworming`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      // Ensure amount defaults to 0 if empty
      const formData = {
        ...form,
        amount: form.amount || "0.00"
      };
      
      await axios.post(getApiUrl(`/api/horses/${horseId}/deworming`), formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setForm({ product_used: "", administered_date: "", fecal_egg_count_result: "", notes: "", amount: "" });
      fetchRecords();
      showSuccess("Deworming record added successfully!");
    } catch (err) {
      console.error("Submit error:", err);
      showError(err.response?.data?.error || "Failed to add deworming record. Please try again.");
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const goBack = () => navigate("/horses");

 const startEdit = (v) => {
  setEditingId(v.id);
  setEditData({
    product_used: v.product_used || "",
    administered_date: v.administered_date ? v.administered_date.split("T")[0] : "",
    fecal_egg_count_result: v.fecal_egg_count_result || "",
    notes: v.notes || "",
    amount: v.Amount || ""
  });
};

  const saveEdit = async (id) => {
    try {
      // Ensure amount defaults to 0 if empty
      const editFormData = {
        ...editData,
        amount: editData.amount || "0.00"
      };
      
      await axios.put(getApiUrl(`/api/horses/${horseId}/deworming/${id}`), editFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setEditingId(null);
      fetchRecords();
      showSuccess("Deworming record updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      showError(err.response?.data?.error || "Failed to update deworming record. Please try again.");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(getApiUrl(`/api/deworming/${recordId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      fetchRecords();
      showSuccess("Deworming record deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      showError(err.response?.data?.error || "Failed to delete deworming record. Please try again.");
    }
  };

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      <button
        onClick={goBack}
        className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
      >
  ← Back to Horses
</button>
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Bug className="mr-2 text-green-600" /> Deworming
      </h2>

 {!editingId && (
   <div className="bg-white shadow p-6 rounded mb-8">
     <div className="flex items-center justify-between mb-4">
       <h3 className="text-lg font-semibold">Add Deworming Record</h3>
       <button
         onClick={() => setShowForm(!showForm)}
         className="text-sm text-blue-600 hover:underline focus:outline-none"
       >
         {showForm ? "Hide" : "Add"}
       </button>
     </div>

     {showForm && (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Product Used *</label>
        <input
          name="product_used"
          placeholder="e.g., Ivermectin, Fenbendazole, Pyrantel"
          value={form.product_used}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
        <p className="text-xs text-gray-500 mt-1">Name of the deworming product</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Administered Date *</label>
        <input
          type="date"
          name="administered_date"
          value={form.administered_date}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
        <p className="text-xs text-gray-500 mt-1">When the deworming was given</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Fecal Egg Count Result</label>
        <input
          name="fecal_egg_count_result"
          placeholder="e.g., 0 eggs per gram, 150 EPG"
          value={form.fecal_egg_count_result}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
        <p className="text-xs text-gray-500 mt-1">Results from fecal egg count test</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
        <CurrencyInput
          name="amount"
          value={form.amount}
          onChange={(val) => setForm({ ...form, amount: val })}
          locale={navigator.language || 'en-US'}
          currency="USD"
          className="border px-3 py-2 rounded w-full"
          placeholder="0.00"
        />
        <p className="text-xs text-gray-500 mt-1">Cost of deworming treatment</p>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <input
          name="notes"
          placeholder="Any additional information about the treatment"
          value={form.notes}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
        <p className="text-xs text-gray-500 mt-1">Additional details or observations</p>
      </div>
    </div>
  )}

       {showForm && (
         <button
           onClick={handleSubmit}
           className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
         >
           Save Deworming
         </button>
       )}
     </div>
   )}


      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">History</h3>
        {records.length === 0 ? (
          <p className="text-gray-500">No records yet.</p>
        ) : (
        <ul className="divide-y divide-gray-200">
          {records.map((rec) => (
            <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              {editingId === rec.id ? (
                <div className="w-full sm:flex-grow">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Used</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="product_used"
                      value={editData.product_used}
                      onChange={(e) => setEditData({ ...editData, product_used: e.target.value })}
                      placeholder="e.g., Ivermectin, Fenbendazole, Pyrantel"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Administered Date *</label>
                    <input
                      type="date"
                      className="border px-2 py-1 rounded w-full"
                      name="administered_date"
                      value={editData.administered_date}
                      onChange={(e) => setEditData({ ...editData, administered_date: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecal Egg Count Result</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="fecal_egg_count_result"
                      value={editData.fecal_egg_count_result}
                      onChange={(e) => setEditData({ ...editData, fecal_egg_count_result: e.target.value })}
                      placeholder="e.g., Low, Moderate, High, or specific count"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="notes"
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      placeholder="Any additional information about the treatment"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                    <CurrencyInput
                      name="amount"
                      value={editData.amount}
                      onChange={(val) => setEditData({ ...editData, amount: val })}
                      locale={navigator.language || 'en-US'}
                      currency="USD"
                      className="border px-2 py-1 rounded w-full"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(rec.id)}
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-300 text-black px-4 py-1 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">{rec.product_used}</p>
                    <p className="text-sm text-gray-600">
                      Administered: {formatWithSystemLocale(rec.administered_date)}
                    </p>
                    {rec.fecal_egg_count_result && (
                      <p className="text-sm text-gray-600">
                        Fecal Egg Count: {rec.fecal_egg_count_result}
                      </p>
                    )}
                    {rec.notes && (
                      <p className="text-sm text-gray-500 italic">Notes: {rec.notes}</p>
                    )}
                    <p className="text-sm text-gray-500 italic">
                      Cost: {rec.Amount ? <FormattedCurrency value={rec.Amount} /> : "No cost recorded"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(rec)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        )}
      </div>
    </div>
  );
}
